/**
 * Patient Settings API - wires every settings tab to real backend endpoints.
 *
 * Announcements  → GET/POST /patients/:id/announcements
 * Deactivate     → POST /patients/:id/deactivate  { reason }
 * Reactivate     → POST /patients/:id/reactivate
 * Merge          → POST /patients/:survivorId/merge  { duplicate_id }
 * Consent        → questionnaire sends + responses (category=consent)
 * FHIR           → see ehi.api.ts
 */

import { apiGet, apiPost, apiPut, apiDelete } from "./client";
import {
  listSendsByPatient,
  listResponsesByPatient,
  listQuestionnaires,
} from "./questionnaire.api";
import type { QuestionnaireSendStatus } from "@/types";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AnnouncementCategory = "billing" | "alert" | "clinical" | "admin";

export interface AnnouncementNote {
  id: string;
  patient_id: string;
  category: AnnouncementCategory;
  title: string;
  body: string;
  status: "active" | "inactive";
  created_by_user_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateAnnouncementPayload {
  category: AnnouncementCategory;
  title: string;
  body: string;
}

export interface MergeSearchResult {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  mrn: string;
  phone?: string;
  email?: string;
  active: boolean;
  merged_into_patient_id?: string;
}

export interface MergePatientResult {
  survivor: {
    id: string;
    first_name: string;
    last_name: string;
    mrn: string;
  };
  duplicate_id: string;
  records_moved: number;
  tables_touched: number;
}

export interface ConsentRow {
  key: string;
  sendId?: string;
  responseId?: string;
  questionnaireId: string;
  formTitle: string;
  sentAt?: string;
  status: QuestionnaireSendStatus;
  signedDate?: string;
  channel?: "portal" | "email" | "sms";
}

/** @deprecated Use ConsentRow */
export type ConsentItem = ConsentRow;

export interface DeactivateResult {
  active: boolean;
  deactivated_at?: string;
  deactivate_reason?: string;
}

// ─── Announcements ────────────────────────────────────────────────────────────

function mapAnnouncement(n: any, patientId?: string): AnnouncementNote {
  return {
    id: n.id,
    patient_id: n.patient_id ?? patientId ?? "",
    category: (n.category ?? "admin") as AnnouncementCategory,
    title: n.title ?? "",
    body: n.body ?? "",
    status: n.status === "active" ? "active" : "inactive",
    created_by_user_id: n.created_by_user_id,
    created_at: n.created_at ?? "",
    updated_at: n.updated_at ?? "",
  };
}

/** POST /api/v1/patients/announcements/summary */
export async function getAnnouncementSummaries(
  patientIds: string[],
): Promise<Record<string, PatientAnnouncementSummary>> {
  if (patientIds.length === 0) return {};
  const unique = [...new Set(patientIds.filter(Boolean))].slice(0, 50);
  const raw = await apiPost<Record<string, PatientAnnouncementSummary>>(
    "/patients/announcements/summary",
    { patient_ids: unique },
  );
  return raw ?? {};
}

export interface PatientAnnouncementSummary {
  patient_id: string;
  count: number;
  primary_category: AnnouncementCategory;
  primary_title: string;
}

/** GET /api/v1/patients/:id/announcements */
export async function getAnnouncements(patientId: string): Promise<AnnouncementNote[]> {
  const raw = await apiGet<any>(`/patients/${patientId}/announcements`);
  const items: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [];
  return items.map((n) => mapAnnouncement(n, patientId));
}

/** GET /api/v1/patients/:id/announcements?active_only=true */
export async function getActiveAnnouncements(patientId: string): Promise<AnnouncementNote[]> {
  const raw = await apiGet<any>(`/patients/${patientId}/announcements`, {
    params: { active_only: "true" },
  });
  const items: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [];
  return items.map((n) => mapAnnouncement(n, patientId));
}

/** POST /api/v1/patients/:id/announcements */
export async function createAnnouncement(
  patientId: string,
  payload: CreateAnnouncementPayload,
): Promise<AnnouncementNote> {
  const n = await apiPost<any>(`/patients/${patientId}/announcements`, payload);
  return mapAnnouncement(n, patientId);
}

/** PUT /api/v1/announcements/:id */
export async function toggleAnnouncement(announcementId: string, active: boolean): Promise<void> {
  await apiPut<any>(`/announcements/${announcementId}`, {
    status: active ? "active" : "inactive",
  });
}

/** DELETE /api/v1/announcements/:id */
export async function deleteAnnouncement(announcementId: string): Promise<void> {
  await apiDelete<any>(`/announcements/${announcementId}`);
}

// ─── Deactivate / Reactivate ──────────────────────────────────────────────────

/** POST /api/v1/patients/:id/deactivate */
export async function deactivatePatient(
  patientId: string,
  reason: string,
): Promise<DeactivateResult> {
  const p = await apiPost<any>(`/patients/${patientId}/deactivate`, { reason });
  return {
    active: p.active ?? false,
    deactivated_at: p.deactivated_at,
    deactivate_reason: p.deactivate_reason,
  };
}

/** POST /api/v1/patients/:id/reactivate */
export async function reactivatePatient(patientId: string): Promise<DeactivateResult> {
  const p = await apiPost<any>(`/patients/${patientId}/reactivate`, {});
  return {
    active: p.active ?? true,
    deactivated_at: p.deactivated_at,
    deactivate_reason: p.deactivate_reason,
  };
}

// ─── Account Merge ────────────────────────────────────────────────────────────

/** GET /api/v1/patients/search?first_name=...&last_name=... */
export async function searchForDuplicate(query: string): Promise<MergeSearchResult[]> {
  const parts = query.trim().split(/\s+/);
  const params: Record<string, string> =
    parts.length === 1
      ? { last_name: parts[0] || "" }
      : { first_name: parts[0] || "", last_name: parts.slice(1).join(" ") };
  params.active = "true";
  params.exclude_merged = "true";

  const raw = await apiGet<any>("/patients/search", { params });
  const items: any[] = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.patients)
    ? raw.patients
    : Array.isArray(raw?.results)
    ? raw.results
    : [];

  return items.map((p: any) => ({
    id: p.id,
    first_name: p.first_name ?? "",
    last_name: p.last_name ?? "",
    date_of_birth: p.date_of_birth ?? "",
    mrn: p.mrn ?? "",
    phone: p.phone,
    email: p.email,
    active: p.active ?? true,
    merged_into_patient_id: p.merged_into_patient_id,
  }));
}

/** POST /api/v1/patients/:survivorId/merge */
export async function mergePatients(
  survivorId: string,
  duplicateId: string,
): Promise<MergePatientResult> {
  return apiPost<MergePatientResult>(`/patients/${survivorId}/merge`, {
    duplicate_id: duplicateId,
  });
}

// ─── Consent (questionnaire sends + responses) ────────────────────────────────

/** Consent questionnaire templates available to send. */
export async function listConsentTemplates() {
  return listQuestionnaires({ category: "consent", per_page: 50 });
}

/** All consent form sends for a patient, enriched with titles and signed dates. */
export async function getConsentRows(patientId: string): Promise<ConsentRow[]> {
  const [templatesPage, sendsPage, responsesPage] = await Promise.all([
    listQuestionnaires({ category: "consent", per_page: 50 }),
    listSendsByPatient(patientId, { per_page: 100 }),
    listResponsesByPatient(patientId, { per_page: 100 }),
  ]);

  const titleById = Object.fromEntries(
    (templatesPage.data ?? []).map((t) => [t.id, t.title]),
  );
  const consentIds = new Set(Object.keys(titleById));

  const responseById = Object.fromEntries(
    (responsesPage.data ?? []).map((r) => [r.id, r]),
  );

  const rows: ConsentRow[] = (sendsPage.data ?? [])
    .filter((s) => consentIds.has(s.questionnaire_id))
    .map((s) => ({
      key: s.id,
      sendId: s.id,
      responseId: s.response_id,
      questionnaireId: s.questionnaire_id,
      formTitle:
        titleById[s.questionnaire_id] ?? s.questionnaire_title ?? "Consent Form",
      sentAt: s.sent_at ?? s.created_at,
      status: s.status,
      signedDate:
        s.completed_at ??
        (s.response_id ? responseById[s.response_id]?.created_at : undefined),
      channel: s.channel,
    }));

  const linkedResponseIds = new Set(
    rows.map((r) => r.responseId).filter(Boolean) as string[],
  );

  for (const r of responsesPage.data ?? []) {
    if (!consentIds.has(r.questionnaire_id) || linkedResponseIds.has(r.id)) continue;
    rows.push({
      key: `response-${r.id}`,
      responseId: r.id,
      questionnaireId: r.questionnaire_id,
      formTitle: titleById[r.questionnaire_id] ?? "Consent Form",
      status: "completed",
      signedDate: r.authored_at ?? r.created_at,
      sentAt: r.created_at,
    });
  }

  return rows.sort(
    (a, b) =>
      new Date(b.sentAt ?? b.signedDate ?? 0).getTime() -
      new Date(a.sentAt ?? a.signedDate ?? 0).getTime(),
  );
}

/** @deprecated Use getConsentRows */
export async function getConsentDocuments(patientId: string): Promise<ConsentRow[]> {
  return getConsentRows(patientId);
}
